import React, {useCallback, useEffect, useState} from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow, Paper, Stack, TextField, Divider, ListItem, ListItemAvatar, IconButton,
} from '@mui/material';
import api from "../../../services/api";
import CircularProgress from "@mui/material/CircularProgress";
import {styled} from '@mui/material/styles';
import {DialogProps} from "@mui/material/Dialog";
import {SendModificationListItem} from "../../EntityDialog/SendModificationListItem";
import {ConfirmationTable} from "../../../modules/service-provider/components/ConfirmationTable";
import {LoadingButton} from "@mui/lab";
import {useStateContext} from "../../../hooks/useStateContext";
import {hasPermissionByPriority} from "../../../core/permissions/hasPermission";
import {ROLE_PRIORITIES} from "../../../core/permissions/permissions";
import {serviceReservationViewTable} from "../../../schema/serviceReservationViewTable";
import {FileField} from "../../../pages/ServiceProviderPages/components/fields/FileField";
import {pendingChanges} from "../../../schema/pendingChanges";
import {FileUploader} from "react-drag-drop-files";
import List from "@mui/material/List";
import Zoom from "react-medium-image-zoom";
import Tooltip from "@mui/material/Tooltip";
import Avatar from "@mui/material/Avatar";
import FolderRoundedIcon from "@mui/icons-material/FolderRounded";
import ListItemText from "@mui/material/ListItemText";
import CloudDownloadRoundedIcon from "@mui/icons-material/CloudDownloadRounded";
import {DeleteRounded} from "@mui/icons-material";
import toast from "react-hot-toast";

interface ConfirmDialogProps {
  open: boolean;
  data: any;
  onClose: () => void;
  onConfirm: () => void;
}

const StyledTableRow = styled(TableRow)(({theme}) => ({
  '&:nth-of-type(odd)': {
    backgroundColor: theme.palette.action.hover,
  },
  // hide last border
  '&:last-child td, &:last-child th': {
    border: 0,
  },
}));

const PendingRowUpdateStatusDialog: React.FC<ConfirmDialogProps> = ({
                                                                      open,
                                                                      data,
                                                                      onClose,
                                                                      onConfirm
                                                                    }) => {
  const [row, setRow] = useState<any[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [fullWidth, setFullWidth] = useState(true);
  const [maxWidth, setMaxWidth] = useState<DialogProps['maxWidth']>('md');
  const {user} = useStateContext();
  const isAdmin = hasPermissionByPriority(user.roles, ROLE_PRIORITIES[1]);
  const [textMessage, setTextMessage] = useState<any>(null);
  const [softLoading, setSoftLoading] = useState<boolean>(false);
  const [files, setFiles] = useState<any>([]);

  const sendToProvider = useCallback(() => {
    setLoading(true);

    let formData = new FormData();
    files.forEach((file: any, i: any) => {
      formData.append(`file-${i}`, file);
    });

    formData.append('message', textMessage);

    api.postFile(`/service-reservations/formatted/${data?.id}/send`, formData).then((result: any) => {
      onConfirm();
    })
      .catch(()=>{
        toast.error("No service group selected");
    })
      .finally(()=>{
      setLoading(false);
    });
  }, [files,textMessage]);

  const fetchData = useCallback(() => {

    setLoading(true);

    api
      .get(`/service-reservations/formatted/${data?.id}`)
      .then((res: any) => {
        setRow(res);
        setLoading(false);
      });

  }, []);

  useEffect(() => {
    fetchData();
  }, []);

  const handleChange = (newFiles: any) => {
    const newFilesArray = [...newFiles];
    setFiles((prev: any) => [
      ...prev,
      ...newFilesArray.map((file: any) => {
        file.preview = URL.createObjectURL(file);
        return file;
      }),
    ]);
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth={fullWidth}
      maxWidth={maxWidth}
    >
      <DialogTitle variant={'h4'}>Are you sure?</DialogTitle>
      <DialogContent>
        <Typography variant="body1">
          The Reservation will be confirmed on the Provider side!
        </Typography>

        <Box sx={{
          marginTop: '20px'
        }}>
          <ConfirmationTable
            item={row}
          />
        </Box>

        {!isAdmin && (
          <Stack
            sx={{
              mt: '10px',
            }}
            gap={1}
          >
            <TextField
              label='Message for approval'
              onChange={(event: any) => {
                setTextMessage(event.target.value);
              }}/>

            <Stack
              sx={{
                opacity: softLoading ? 0.5 : 1,
                pointerEvents: softLoading ? 'none' : 'auto',
              }}
            >
              <FileUploader
                multiple={true}
                handleChange={handleChange}
                name="file"
                classes={'w-full !max-w-[100%] !min-w-[100px]'}
              />
            </Stack>
            <Divider/>
            <List
              component="div"
              aria-labelledby="Files"
              sx={{
                opacity: softLoading ? 0.5 : 1,
                pointerEvents: softLoading ? 'none' : 'auto',
                width: '100%',
              }}
            >
              {files.map((item: any, index: number) => {
                return (
                  <>
                    <ListItem
                      sx={{
                        borderBottom: '1px solid #f3f3f3',
                        padding: '8px',
                      }}
                      key={`list-images-${index}`}
                      id={`list-images-${index}`}
                    >
                      <ListItemAvatar
                        sx={{
                          '& .MuiAvatar-root': {
                            border: '1px solid #e1e1e1',
                            borderRadius: '4px',
                            width: '50px',
                            height: '50px',
                          },
                        }}
                      >
                        {item?.type.includes('image') ? (
                          <Zoom>
                            <Tooltip title={item?.name}>
                              <Avatar src={item?.link} alt={item?.name}/>
                            </Tooltip>
                          </Zoom>
                        ) : (
                          <div
                            className={
                              'w-[50px] h-[50px] rounded-[4px] bg-[#e1e1e1] relative'
                            }
                          >
                            <FolderRoundedIcon
                              sx={{
                                color: '#b9b9b9',
                                position: 'absolute',
                                top: '50%',
                                left: '50%',
                                transform: 'translate(-50%,-50%)',
                              }}
                            />
                          </div>
                        )}
                      </ListItemAvatar>
                      <ListItemText
                        secondary={
                          <React.Fragment>
                            <Tooltip title={item?.name}>
                              <Typography
                                variant={'caption'}
                                color={'gray'}
                                sx={{
                                  whiteSpace: 'nowrap',
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                  display: 'block'
                                }}
                              >
                                {item?.name}
                              </Typography>
                            </Tooltip>
                          </React.Fragment>
                        }
                      />
                      <Stack
                        direction={'row'}
                        sx={{
                          width: '100px',
                        }}
                      >
                        <IconButton
                          onClick={() => {

                          }}
                        >
                          <DeleteRounded color={'error'}/>
                        </IconButton>
                      </Stack>
                    </ListItem>
                  </>
                );
              })}
            </List>
          </Stack>
        )}

      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <LoadingButton
          loading={loading}
          variant="contained" color="primary" onClick={() => sendToProvider()} autoFocus>
          Confirm
        </LoadingButton>
      </DialogActions>
    </Dialog>
  );
};

export default PendingRowUpdateStatusDialog;
